import { type SearchScope } from '@bizzdesign/sdk-search';
import { type Scope } from '@bizzdesign/shared/models';
import { type SearchTabEmptyStateLink } from '../components/search-tab-empty-state/search-tab-empty-state.component';

const ALL_SCOPE_EMPTY_STATE_LINKS: readonly SearchTabEmptyStateLink[] = [
  { labelKey: 'featureSearch.quickFilters.object', scope: 'OBJECT' },
  { labelKey: 'featureSearch.quickFilters.view', scope: 'VIEW' },
  { labelKey: 'featureSearch.quickFilters.workspace', scope: 'WORKSPACE' },
];

/**
 * Zeroes the counts of scopes excluded by the active scope filters, so the
 * per-scope counts match what the scoped result queries actually return.
 *
 * The count query counts every scope by term regardless of scope filters,
 * whereas the view/workspace result queries return nothing for an out-of-scope
 * search; this reconciles the two.
 *
 * @param countsPerScope - Raw result counts per search scope.
 * @param scopeFilters - Active scope filters; an empty array means no scoping.
 * @returns A new counts map with out-of-scope counts set to `0`.
 */
export function applyScopeFilterToCounts(
  countsPerScope: Map<SearchScope, number>,
  scopeFilters: SearchScope[],
): Map<SearchScope, number> {
  if (scopeFilters.length === 0) {
    return countsPerScope;
  }
  return new Map(
    [...countsPerScope.entries()].map(([scope, count]) => [
      scope,
      scopeFilters.includes(scope) ? count : 0,
    ]),
  );
}

/**
 * Returns the only scope that has search results, or `null` when zero or
 * multiple scopes have results.
 *
 * @param countsPerScope - Result counts per search scope.
 * @returns The single scope with results, or `null`.
 */
export function getSingleResultScope(
  countsPerScope: Map<SearchScope, number>,
): SearchScope | null {
  const scopesWithResults = [...countsPerScope.entries()]
    .filter(([, count]) => count > 0)
    .map(([scope]) => scope);
  return scopesWithResults.length === 1 ? scopesWithResults[0] : null;
}

/**
 * Builds the empty-state cross-links a search tab shows: the *other* scopes
 * (never the tab's own) that currently have results.
 *
 * @param ownScope - The scope of the tab rendering the empty state.
 * @param scopesWithResults - Scopes that currently have matches.
 * @returns Links to the sibling tabs that have results.
 */
export function crossScopeEmptyStateLinks(
  ownScope: SearchScope,
  scopesWithResults: SearchScope[],
): SearchTabEmptyStateLink[] {
  return ALL_SCOPE_EMPTY_STATE_LINKS.filter(
    (link) => link.scope !== ownScope && scopesWithResults.includes(link.scope),
  );
}

/**
 * Decides which tab to auto-select when exactly one scope has results.
 *
 * Dedupes on the counts' content, skips a manual tab pick, and skips
 * advanced-filter *preview* counts.
 *
 * @param input - The current scoped counts and the guard state.
 * @returns The key to remember as processed and the scope to select (or `null`).
 * @remarks
 * `processedKey` only advances past the preview guard, so a preview count seen
 * while the advanced-filter drawer is open never dedupes the identical applied
 * count once the drawer closes.
 */
export function resolveSingleResultTabSelection(input: {
  scopedCounts: Map<SearchScope, number>;
  lastProcessedKey: string | undefined;
  userSelectedTab: boolean;
  advancedFilterVisible: boolean;
}): { processedKey: string | undefined; scopeToSelect: SearchScope | null } {
  const countsKey = [...input.scopedCounts.entries()]
    .map(([scope, count]) => `${scope}:${count}`)
    .join(',');
  if (
    countsKey === input.lastProcessedKey ||
    input.userSelectedTab ||
    input.advancedFilterVisible
  ) {
    return { processedKey: input.lastProcessedKey, scopeToSelect: null };
  }
  return {
    processedKey: countsKey,
    scopeToSelect: getSingleResultScope(input.scopedCounts),
  };
}

/**
 * Decides whether switching tabs should close the open details drawer: it should when the
 * drawer is open and the open item's scope is not shown on the target tab.
 *
 * @param acceptedScope - The scope the target tab shows, or `undefined` for the All tab
 *   (which shows every scope and so never closes the drawer).
 * @param openItemScope - The scope of the item whose details are open, if any.
 * @param isDrawerOpen - Whether the details drawer is currently open.
 * @returns `true` when the drawer should be closed.
 */
export function shouldCloseDetailsOnTabChange(
  acceptedScope: Scope | undefined,
  openItemScope: Scope | undefined,
  isDrawerOpen: boolean,
): boolean {
  return (
    isDrawerOpen &&
    acceptedScope !== undefined &&
    openItemScope !== undefined &&
    acceptedScope !== openItemScope
  );
}
